import dayjs from 'dayjs';

import type { AnomalyRange, TimeSeries } from '@controlrooms/models';

/**
 * Apply anomaly severity from anomaly ranges to timeseries data points.
 * Uses optimized linear search with early exit for O(n × m) worst case,
 * but much better average case due to sorted anomalies.
 *
 * For each timeseries point, finds all overlapping anomaly ranges and assigns
 * the maximum severity value. This is used for highlighting anomalous regions
 * in chart visualizations.
 *
 * @param timeseries - Array of timeseries data points
 * @param anomalies - Array of anomaly ranges with severity information
 * @returns Timeseries array with severity updated based on anomaly ranges
 */
export const applyAnomalyRangesToTimeseries = (
  timeseries: TimeSeries[],
  anomalies?: AnomalyRange[],
): TimeSeries[] => {
  if (!anomalies?.length) return timeseries;

  // OPTIMIZATION: Pre-process anomalies to avoid repeated dayjs conversions
  // Sort by start time to enable early exit optimization
  const processedAnomalies = anomalies
    .map((a) => ({
      start: dayjs(a.start).valueOf(),
      end: dayjs(a.end).valueOf(),
      severity: a.severity ?? 0,
    }))
    .filter((a) => a.severity > 0) // Skip anomalies with no severity
    .sort((a, b) => a.start - b.start); // Sort by start time for early exit

  if (!processedAnomalies.length) return timeseries;

  return timeseries.map((pt) => {
    const t = dayjs(pt.time).valueOf();
    let maxSeverity = pt.severity ?? 0;

    // Linear search through sorted anomalies
    // Early exit when we encounter an anomaly that starts after current time
    for (const anomaly of processedAnomalies) {
      // Early exit: since sorted by start, if this anomaly starts after t,
      // all subsequent anomalies will also start after t
      if (anomaly.start > t) break;

      // Check if time point falls within this anomaly range
      if (t >= anomaly.start && t <= anomaly.end) {
        maxSeverity = Math.max(maxSeverity, anomaly.severity);
      }
    }

    return {
      ...pt,
      severity: maxSeverity,
    };
  });
};
